/**
 * Determine whether a content item is a language-toggle (`Lang`) marker, by
 * `__typename`, `contentType` (string or `{ id }`), or `type`.
 * @param {Object} content - A content item to inspect
 * @returns {boolean} - True when the item is a `Lang` marker
 */
export const isLanguageToggle = content => {
  const normalizedContentTypeId =
    typeof content?.contentType === 'string'
      ? content.contentType.toLowerCase()
      : String(content?.contentType?.id || '').toLowerCase()
  const normalizedType = String(content?.type || '').toLowerCase()

  return (
    content?.__typename === 'Lang' ||
    normalizedContentTypeId === 'lang' ||
    normalizedType === 'lang'
  )
}

/**
 * Helper function to find language toggle in content array
 * @param {Array|Object} details - Can be either an array directly or an object with a content property
 * @returns {Object|null} - The language toggle object if found, null otherwise
 */
export const getLanguageToggle = details => {
  if (!details) return null

  // If details is an array, search directly
  if (Array.isArray(details)) {
    return details.find(isLanguageToggle)
  }

  // If details has a content property, search in that
  if (details.content && Array.isArray(details.content)) {
    return details.content.find(isLanguageToggle)
  }

  return null
}

/**
 * Determine whether the English/Spanish language toggle should be enabled for a
 * given language-toggle entry.
 *
 * Resolution order:
 * 1. An explicit `boundary` boolean always wins.
 * 2. Otherwise, when either-locale fields are present, require BOTH locales.
 * 3. Otherwise, only when the entry is an actual `Lang` marker (a bare
 *    `{ __typename: 'Lang' }` returned by the content-service
 *    learningExperience payload), treat its presence as the availability
 *    signal and enable the toggle. Unrelated/empty objects return false.
 *
 * @param {Object|null} languageToggle - The resolved language-toggle entry
 * @returns {boolean} - Whether the toggle can be shown
 */
export const resolveCanToggleLanguage = languageToggle => {
  if (!languageToggle) return false

  if (typeof languageToggle.boundary === 'boolean') {
    return languageToggle.boundary
  }

  const hasEnglishField =
    languageToggle.enUs !== undefined || languageToggle.en_us !== undefined
  const hasSpanishField =
    languageToggle.esUs !== undefined || languageToggle.es_us !== undefined

  if (hasEnglishField || hasSpanishField) {
    return !!(
      (languageToggle.enUs ?? languageToggle.en_us) &&
      (languageToggle.esUs ?? languageToggle.es_us)
    )
  }

  return isLanguageToggle(languageToggle)
}
